// src/sectors/pilates/members/modal/detail/tabs/PackageTab.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { PillIcon } from 'shared/ui/soft/PillIcon';
import { TabPanel } from 'shared/ui/soft/TabPanel';

import { money } from 'sectors/pilates/members/components/paymentReceive/ui';
import { dateTR } from '../utils/memberDetailUtils';
import { PackageCreateModal } from '../../PackageCreateModal/PackageCreateModal';
import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';
import { PackageEditModal } from './PackageEditModal';
import { uiBus } from 'shared/api/uiBus';

/* ---------------- helpers ---------------- */

const PAY = {
  PAID: { label: 'Ödendi', color: 'success' },
  UNPAID: { label: 'Ödenmedi', color: 'error' },
  PARTIAL: { label: 'Kısmi', color: 'warning' },
  UNKNOWN: { label: 'Bilinmiyor', color: 'default' },
};

const STATUS = {
  ACTIVE: { label: 'Aktif', color: 'success' },
  USED: { label: 'Bitti', color: 'default' },
  PENDING: { label: 'Beklemede', color: 'warning' },
  CANCELLED: { label: 'İptal', color: 'error' },
};

function titleOf(p) {
  return p?.planName || p?.name || p?.packageName || `#${p?.id ?? '—'}`;
}
function dateOf(p) {
  return p?.createdAt || p?.startAt || p?.soldAt || p?.date || null;
}
function payOf(p) {
  const total = p?.agreedPriceTry ?? p?.planPriceTry ?? 0;
  const paid = p?.paidTry ?? 0;
  const debt =
    typeof p?.debtTry === 'number'
      ? p.debtTry
      : Math.max(0, Number(total || 0) - Number(paid || 0));

  return {
    code: p?.paymentStatus || 'UNKNOWN',
    total,
    paid,
    debt,
  };
}

function remainingOfItem(it) {
  const rs = Math.max(0, (it?.totalSessions ?? 0) - (it?.usedSessions ?? 0));
  const rm = Math.max(0, (it?.totalMakeUp ?? 0) - (it?.usedMakeUp ?? 0));
  const rg = Math.max(0, (it?.totalGift ?? 0) - (it?.usedGift ?? 0));
  return { rs, rm, rg, total: rs + rm + rg };
}

function remainingTotals(items) {
  const list = Array.isArray(items) ? items : [];
  return list.reduce(
    (acc, it) => {
      const r = remainingOfItem(it);
      acc.s += r.rs;
      acc.m += r.rm;
      acc.g += r.rg;
      return acc;
    },
    { s: 0, m: 0, g: 0 }
  );
}

function MiniChip({ label, color = 'default', sx }) {
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant="outlined"
      sx={{
        borderRadius: '999px',
        fontWeight: 800,
        ...sx,
      }}
    />
  );
}

function Stat({ label, value, tone, typo }) {
  return (
    <MDBox sx={{ display: 'grid', gap: 0.1, minWidth: 0 }}>
      <MDTypography
        variant="caption"
        sx={{ ...typo.sub, color: 'text.secondary', lineHeight: 1.1 }}
      >
        {label}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="bold"
        sx={{
          ...typo.stat,
          color: tone || 'text.primary',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </MDTypography>
    </MDBox>
  );
}

/* ---------------- detail: ONLY items breakdown ---------------- */

function ItemsBreakdown({ items = [], theme, typo, isMobile }) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return (
      <MDTypography variant="caption" sx={{ ...typo.sub, color: 'text.secondary' }}>
        Paket içeriği yok.
      </MDTypography>
    );
  }

  const line = (label, used, total) => {
    const t = Number(total ?? 0);
    const u = Number(used ?? 0);
    if (t <= 0) return null;

    return (
      <MDTypography variant="caption" sx={{ ...typo.sub, color: 'text.secondary' }}>
        {label}: <b style={{ color: theme.palette.text.primary }}>{t}</b>{' '}
        <span style={{ opacity: 0.75 }}>({u} kullanıldı)</span>
      </MDTypography>
    );
  };

  return (
    <MDBox sx={{ display: 'grid', gap: 0.9 }}>
      {list.map((it, idx) => {
        const name = it?.activityName || '—';
        const r = remainingOfItem(it);

        return (
          <MDBox
            key={it?.id ?? `${name}-${idx}`}
            sx={{
              borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`,
              background:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.85)
                  : theme.palette.background.paper,
              p: isMobile ? 1.0 : 1.1,
              display: 'grid',
              gap: 0.3,
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
              <MDTypography
                fontWeight="bold"
                sx={{
                  ...typo.rowTitle,
                  lineHeight: 1.15,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </MDTypography>

              <MDTypography
                variant="caption"
                fontWeight="bold"
                sx={{
                  ...typo.sub,
                  color: r.total > 0 ? theme.palette.success.main : theme.palette.text.secondary,
                  whiteSpace: 'nowrap',
                }}
              >
                Kalan: {r.total}
              </MDTypography>
            </MDBox>

            {line('Seans', it?.usedSessions, it?.totalSessions)}
            {line('Telafi', it?.usedMakeUp, it?.totalMakeUp)}
            {line('Hediye', it?.usedGift, it?.totalGift)}
          </MDBox>
        );
      })}
    </MDBox>
  );
}

/* ---------------- accordion row ---------------- */

function PackageAccordion({
  p,
  theme,
  expanded,
  onToggle,
  typo,
  isMobile,
  onEdit,
  onFinish,
  onActivate,
  onDelete,
}) {
  const title = titleOf(p);
  const dt = dateOf(p);
  const pay = payOf(p);

  const payChip = PAY[pay?.code] || PAY.UNKNOWN;
  const st = String(p?.status || 'UNKNOWN').toUpperCase();
  const statusChip = STATUS[st] || { label: st === 'UNKNOWN' ? '—' : st, color: 'default' };

  const source = String(p?.source || '').toUpperCase();
  const sourceLabel =
    source === 'CUSTOM' ? 'Özel' : source === 'LIBRARY' ? 'Kütüphane' : source || '';

  const remaining = remainingTotals(p?.items);
  const remainingTotal = remaining.s;

  const debtTone = pay.debt > 0 ? theme.palette.error.main : theme.palette.success.main;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Icon sx={{ color: theme.palette.text.secondary }}>
            {expanded ? 'expand_less' : 'expand_more'}
          </Icon>
        }
        sx={{
          px: isMobile ? 1.1 : 1.5,
          py: isMobile ? 1.0 : 1.25,
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.35)
              : alpha(theme.palette.background.paper, 0.9),
          '& .MuiAccordionSummary-content': { my: 0, minWidth: 0 },
        }}
      >
        <MDBox sx={{ width: '100%', display: 'grid', gap: 0.75, minWidth: 0 }}>
          {/* Title row */}
          <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography
                fontWeight="bold"
                sx={{
                  ...typo.title,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </MDTypography>

              <MDTypography variant="caption" sx={{ ...typo.sub, color: 'text.secondary' }}>
                {dt ? dateTR(dt) : '—'} • Kap: {p?.classCapacity ?? 1}
              </MDTypography>
            </MDBox>

            {/* Chips: desktop same row, mobile wraps nicely */}
            <MDBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              {/* Action icons */}
              <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title="Paketi düzenle">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // accordion açılmasın
                      onEdit?.(p);
                    }}
                    sx={{
                      color: theme.palette.info.main,
                    }}
                  >
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                </Tooltip>

                {/* PASİF / BİTİR */}
                {['ACTIVE', 'PENDING'].includes(p?.status) && (
                  <Tooltip title="Paketi bitir">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFinish?.(p);
                      }}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <Icon fontSize="small">block</Icon>
                    </IconButton>
                  </Tooltip>
                )}

                {/* AKTİF ET */}
                {['USED', 'PENDING'].includes(p?.status) && (
                  <Tooltip title="Paketi aktif et">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate?.(p);
                      }}
                      sx={{ color: theme.palette.success.main }}
                    >
                      <Icon fontSize="small">play_circle</Icon>
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Paketi sil">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(p);
                    }}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>

              <MiniChip
                label={payChip.label}
                color={payChip.color}
                sx={{ '& .MuiChip-label': { fontSize: typo.chip.fontSize } }}
              />
              {statusChip.label !== '—' ? (
                <MiniChip
                  label={statusChip.label}
                  color={statusChip.color}
                  sx={{ '& .MuiChip-label': { fontSize: typo.chip.fontSize } }}
                />
              ) : null}
              {sourceLabel ? (
                <MiniChip
                  label={sourceLabel}
                  sx={{ '& .MuiChip-label': { fontSize: typo.chip.fontSize } }}
                />
              ) : null}
            </MDBox>
          </MDBox>

          {/* Compact stats */}
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? 'repeat(2, minmax(0, 1fr))'
                : 'repeat(5, minmax(0, 1fr))',
              gap: isMobile ? 0.9 : 1.0,
              alignItems: 'start',
            }}
          >
            <Stat label="Paket" value={money(p?.planPriceTry)} typo={typo} />
            <Stat label="Anlaşılan" value={money(p?.agreedPriceTry)} typo={typo} />
            <Stat label="Ödenen" value={money(pay?.paid)} typo={typo} />
            <Stat label="Borç" value={money(pay?.debt)} tone={debtTone} typo={typo} />
            <Stat
              label="Kalan hak"
              value={String(remainingTotal)}
              tone={remainingTotal > 0 ? theme.palette.success.main : theme.palette.text.secondary}
              typo={typo}
            />
          </MDBox>

          {/* Tiny breakdown hint */}
          <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {remainingTotal > 0 ? (
              <MDTypography variant="caption" sx={{ ...typo.sub, color: 'text.secondary' }}>
                S:{remaining.s} • T:{remaining.m} • H:{remaining.g}
              </MDTypography>
            ) : null}
          </MDBox>
        </MDBox>
      </AccordionSummary>

      <AccordionDetails sx={{ px: isMobile ? 1.1 : 1.5, py: isMobile ? 1.1 : 1.25 }}>
        <MDBox sx={{ display: 'grid', gap: 0.9 }}>
          <MDTypography variant="button" fontWeight="bold" sx={{ ...typo.section }}>
            Paket içeriği
          </MDTypography>

          <ItemsBreakdown items={p?.items} theme={theme} typo={typo} isMobile={isMobile} />

          <Divider />

          <MDTypography variant="caption" sx={{ ...typo.sub, color: 'text.secondary' }}>
            * Detayda yalnızca hak/kullanım gösterilir.
          </MDTypography>
        </MDBox>
      </AccordionDetails>
    </Accordion>
  );
}

/* ---------------- tab ---------------- */

export function PackageTab({ tab, theme: themeProp, packagesState, memberId }) {
  const muiTheme = useMuiTheme();
  const theme = themeProp || muiTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { loading, err, packages = [], fetchPackages } = packagesState || {};
  const [expandedId, setExpandedId] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [finishTarget, setFinishTarget] = React.useState(null);
  const [finishing, setFinishing] = React.useState(false);
  const [activateTarget, setActivateTarget] = React.useState(null);
  const [activating, setActivating] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);
  const handleActivate = (pkg) => {
    setActivateTarget(pkg);
  };

  const typo = {
    headerTitle: { fontSize: isMobile ? '0.98rem' : '1.05rem' },
    headerSub: { fontSize: isMobile ? '0.72rem' : '0.80rem' },
    title: { fontSize: isMobile ? '0.94rem' : '1.02rem' },
    rowTitle: { fontSize: isMobile ? '0.90rem' : '0.96rem' },
    sub: { fontSize: isMobile ? '0.70rem' : '0.78rem' },
    stat: { fontSize: isMobile ? '0.74rem' : '0.80rem' },
    chip: { fontSize: isMobile ? '0.68rem' : '0.72rem' },
    section: { fontSize: isMobile ? '0.86rem' : '0.92rem' },
  };

  const border = `1px solid ${theme.palette.divider}`;
  const [editTarget, setEditTarget] = React.useState(null);

  const handleEdit = (pkg) => {
    setEditTarget(pkg);
  };

  const handleFinish = (pkg) => {
    setFinishTarget(pkg);
  };
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteErr, setDeleteErr] = React.useState(null);

  const [deleteNeedsConfirm, setDeleteNeedsConfirm] = React.useState(false);
  const [deleteMessage, setDeleteMessage] = React.useState(null);
  const handleDelete = (pkg) => {
    setDeleteErr(null);
    setDeleteNeedsConfirm(false);
    setDeleteMessage(null);
    setDeleteTarget(pkg);
  };

  return (
    <TabPanel value={tab} index={2}>
      <SoftCard sx={{ p: isMobile ? 1.25 : 2 }}>
        {/* toolbar */}
        <MDBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.25,
            flexWrap: 'wrap',
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <PillIcon icon="inventory_2" tone="violet" />
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography
                variant="h6"
                fontWeight="bold"
                sx={{ ...typo.headerTitle, lineHeight: 1.12 }}
              >
                Paketler
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{
                  ...typo.headerSub,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Özet + içerik
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
            <MDButton
              variant="gradient"
              color="success"
              size={isMobile ? 'small' : 'medium'}
              sx={{ borderRadius: '14px', px: isMobile ? 1.2 : 1.6 }}
              onClick={() => setCreateOpen(true)}
              disabled={loading || !memberId}
            >
              <Icon sx={{ mr: isMobile ? 0 : 0.5, fontSize: 18 }}>add</Icon>
              {!isMobile ? 'Paket Oluştur' : null}
            </MDButton>

            <MDButton
              variant="outlined"
              color="dark"
              size={isMobile ? 'small' : 'medium'}
              sx={{ borderRadius: '14px', px: isMobile ? 1.2 : 1.6, minWidth: isMobile ? 44 : 110 }}
              onClick={fetchPackages}
              disabled={loading || !fetchPackages}
            >
              <Icon sx={{ mr: isMobile ? 0 : 0.5, fontSize: 18 }}>refresh</Icon>
              {!isMobile ? 'Yenile' : null}
            </MDButton>
          </MDBox>
        </MDBox>

        {/* states */}
        {loading ? (
          <MDBox sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={26} />
            <MDTypography
              variant="caption"
              sx={{ mt: 1, ...typo.headerSub, color: 'text.secondary' }}
            >
              Paketler yükleniyor…
            </MDTypography>
          </MDBox>
        ) : err ? (
          <MDBox
            sx={{
              p: 1.25,
              borderRadius: '16px',
              border,
              background: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.08 : 0.04
              ),
            }}
          >
            <MDTypography
              variant="button"
              fontWeight="bold"
              sx={{ color: theme.palette.error.main }}
            >
              {err}
            </MDTypography>
            <MDTypography variant="caption" sx={{ ...typo.headerSub, color: 'text.secondary' }}>
              Tekrar denemek için “Yenile”ye bas.
            </MDTypography>
          </MDBox>
        ) : !Array.isArray(packages) || packages.length === 0 ? (
          <MDBox
            sx={{
              p: 1.25,
              borderRadius: '16px',
              border,
              background: alpha(
                theme.palette.info.main,
                theme.palette.mode === 'dark' ? 0.06 : 0.03
              ),
            }}
          >
            <MDTypography variant="button" fontWeight="bold">
              Paket yok
            </MDTypography>
            <MDTypography variant="caption" sx={{ ...typo.headerSub, color: 'text.secondary' }}>
              Üyeye paket tanımlandığında burada listelenecek.
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {packages.map((p) => {
              const id = String(p?.id ?? titleOf(p));
              const expanded = expandedId === id;

              return (
                <PackageAccordion
                  key={id}
                  p={p}
                  theme={theme}
                  expanded={expanded}
                  onToggle={() => setExpandedId(expanded ? null : id)}
                  typo={typo}
                  isMobile={isMobile}
                  onEdit={handleEdit}
                  onFinish={handleFinish}
                  onActivate={handleActivate}
                  onDelete={handleDelete}
                />
              );
            })}
          </MDBox>
        )}

        <PackageCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          memberId={memberId}
          onCreated={() => {
            setCreateOpen(false);
            fetchPackages?.();
          }}
        />
        <PackageEditModal
          open={!!editTarget}
          onClose={() => {
            setEditTarget(null);
          }}
          memberId={memberId}
          pkg={editTarget}
          onUpdated={() => {
            setEditTarget(null);
            fetchPackages?.();
          }}
        />
        <Dialog open={!!finishTarget} onClose={() => setFinishTarget(null)}>
          <DialogTitle>Paketi Bitir</DialogTitle>

          <DialogContent>
            <MDTypography variant="body2">
              <b>{finishTarget?.planName}</b> paketini bitirmek istediğinize emin misiniz?
            </MDTypography>

            <MDTypography
              variant="caption"
              sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
            >
              Bu işlem geri alınamaz.
            </MDTypography>
          </DialogContent>

          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={() => setFinishTarget(null)}
              disabled={finishing}
            >
              Vazgeç
            </MDButton>

            <MDButton
              variant="gradient"
              color="error"
              loading={finishing}
              onClick={async () => {
                try {
                  setFinishing(true);

                  await membersApi.finishMemberPackage(memberId, finishTarget.id);

                  setFinishTarget(null);
                  fetchPackages?.();

                  uiBus.emit('toast', {
                    type: 'success',
                    message: 'Paket başarıyla bitirildi',
                  });
                } catch (e) {
                  uiBus.emit('toast', {
                    type: 'error',
                    message: e?.response?.data?.message || e?.message || 'Paket bitirilemedi',
                  });
                } finally {
                  setFinishing(false);
                }
              }}
            >
              Paketi Bitir
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog open={!!activateTarget} onClose={() => setActivateTarget(null)}>
          <DialogTitle>Paketi Aktif Et</DialogTitle>

          <DialogContent>
            <MDTypography variant="body2">
              <b>{activateTarget?.planName}</b> paketini aktif etmek istiyor musunuz?
            </MDTypography>

            {actionError && (
              <MDTypography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                {actionError}
              </MDTypography>
            )}
          </DialogContent>

          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={() => {
                setActivateTarget(null);
                setActionError(null);
              }}
              disabled={activating}
            >
              Vazgeç
            </MDButton>

            <MDButton
              variant="gradient"
              color="success"
              loading={activating}
              onClick={async () => {
                try {
                  setActivating(true);
                  setActionError(null);

                  await membersApi.activateMemberPackage(memberId, activateTarget.id);

                  setActivateTarget(null);
                  fetchPackages?.();

                  uiBus.emit('toast', {
                    type: 'success',
                    message: 'Paket aktif edildi',
                  });
                } catch (e) {
                  const message =
                    e?.response?.data?.message || 'Üyenin aktif başka bir paketi var.';

                  setActionError(message);

                  uiBus.emit('toast', {
                    type: 'error',
                    message,
                  });
                } finally {
                  setActivating(false);
                }
              }}
            >
              Aktif Et
            </MDButton>
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={!!deleteTarget}
          tone={deleteErr ? 'error' : 'warning'}
          title={deleteErr ? 'Paket silinemedi' : 'Paketi sil'}
          subtitle={deleteTarget?.planName || deleteTarget?.packageName || 'Paket'}
          message={
            deleteErr
              ? `Hata: ${deleteErr}`
              : deleteMessage || 'Bu paketi silmek istediğine emin misin? Bu işlem geri alınamaz.'
          }
          confirmText={deleting ? 'Siliniyor...' : deleteNeedsConfirm ? 'Evet, yine de sil' : 'Sil'}
          cancelText="Vazgeç"
          busy={deleting}
          onCancel={() => {
            if (deleting) return;
            setDeleteTarget(null);
            setDeleteErr(null);
            setDeleteNeedsConfirm(false);
            setDeleteMessage(null);
          }}
          onConfirm={async () => {
            if (!memberId || !deleteTarget?.id) return;

            // ✅ 2. tıklama: kullanıcı zaten “Evet yine de sil” dedi
            if (deleteNeedsConfirm) {
              try {
                setDeleting(true);
                setDeleteErr(null);

                await membersApi.deleteMemberPackage(memberId, deleteTarget.id, { force: true });

                setDeleteTarget(null);
                setDeleteNeedsConfirm(false);
                setDeleteMessage(null);
                fetchPackages?.();

                uiBus.emit('toast', {
                  type: 'success',
                  message: 'Paket silindi',
                });
              } catch (e) {
                const message = e?.response?.data?.message || e?.message || 'Silme başarısız.';
                setDeleteErr(message);

                uiBus.emit('toast', {
                  type: 'error',
                  message,
                });
              } finally {
                setDeleting(false);
              }
              return;
            }

            // ✅ 1. tıklama: önce validate
            try {
              setDeleteErr(null);

              const v = await membersApi.validateDeleteMemberPackage(memberId, deleteTarget.id);
              // beklenen: { allowed, needConfirm, message }

              if (!v?.allowed) {
                setDeleteErr(v?.message || 'Paket silinemiyor.');
                return;
              }

              if (v?.needConfirm) {
                setDeleteNeedsConfirm(true);
                setDeleteMessage(
                  v?.message || 'Bu pakette kullanım var. Yine de silmek ister misin?'
                );
                return;
              }

              // ✅ confirm gerekmiyorsa direkt commit
              setDeleting(true);
              await membersApi.deleteMemberPackage(memberId, deleteTarget.id, { force: false });

              setDeleteTarget(null);
              setDeleteNeedsConfirm(false);
              setDeleteMessage(null);
              fetchPackages?.();

              uiBus.emit('toast', {
                type: 'success',
                message: 'Paket silindi',
              });
            } catch (e) {
              const message = e?.response?.data?.message || e?.message || 'Silme başarısız.';
              setDeleteErr(message);

              uiBus.emit('toast', {
                type: 'error',
                message,
              });
            } finally {
              setDeleting(false);
            }
          }}
        />
      </SoftCard>
    </TabPanel>
  );
}
